import {
  type BridgeSeat,
  type BridgeSuit,
  CRAZY_EIGHTS_TARGET,
  cardId,
  crazyEightsViewFor,
  sortHand,
} from '@chessmate/game-engine';
import { CARD_WIDTH_CLASS, CardBack, PlayingCard, SUIT_SYMBOL, HAND_CLASS } from '../components/PlayingCard.js';
import { OpponentTable } from '../components/table/OpponentTable.js';
import { RoundSummaryPanel } from '../components/table/RoundSummaryPanel.js';
import { ScoreStrip } from '../components/table/ScoreStrip.js';
import { SeatPlate } from '../components/table/SeatPlate.js';
import { pointsSummary } from '../components/table/pointsSummary.js';
import { useSeatIdentity } from '../components/table/useSeatIdentity.js';
import { useT } from '../i18n/useT.js';
import { HUMAN_SEAT, type PlayerCount, useCrazyEightsStore } from '../state/crazy-eights-store.js';
import { useSeatBubbles } from '../state/table-chatter.js';

const SUITS: readonly BridgeSuit[] = ['S', 'H', 'D', 'C'];

/**
 * The Crazy Eights screen: you in South, robots in the other seats. It renders only from
 * `crazyEightsViewFor(game, HUMAN_SEAT)`, so a hand you may not see is never in the data this component can reach.
 * Rules: match the suit or number on the pile, an eight is wild (and so are the Jokers, when switched on), draw one card when stuck, first to empty their hand wins.
 */
export function CrazyEightsScreen({ onExit }: { onExit: () => void }) {
  const { t } = useT('games');
  const game = useCrazyEightsStore((s) => s.game);
  const result = useCrazyEightsStore((s) => s.result);
  const totals = useCrazyEightsStore((s) => s.totals);
  const gameWinner = useCrazyEightsStore((s) => s.gameWinner);
  const handNumber = useCrazyEightsStore((s) => s.handNumber);
  const seed = useCrazyEightsStore((s) => s.seed);
  const players = useCrazyEightsStore((s) => s.players);
  const strength = useCrazyEightsStore((s) => s.strength);
  const fourColour = useCrazyEightsStore((s) => s.fourColour);
  const jokers = useCrazyEightsStore((s) => s.jokers);
  const pendingEight = useCrazyEightsStore((s) => s.pendingEight);
  const tap = useCrazyEightsStore((s) => s.tap);
  const chooseSuit = useCrazyEightsStore((s) => s.chooseSuit);
  const cancelEight = useCrazyEightsStore((s) => s.cancelEight);
  const draw = useCrazyEightsStore((s) => s.draw);
  const pass = useCrazyEightsStore((s) => s.pass);
  const nextHand = useCrazyEightsStore((s) => s.nextHand);
  const newGame = useCrazyEightsStore((s) => s.newGame);
  const setStrength = useCrazyEightsStore((s) => s.setStrength);
  const setFourColour = useCrazyEightsStore((s) => s.setFourColour);
  const setJokers = useCrazyEightsStore((s) => s.setJokers);
  const setPlayers = useCrazyEightsStore((s) => s.setPlayers);

  const identify = useSeatIdentity(['S', 'W', 'N', 'E'], (x) => t(`cardgame-seat-${x}`));
  const bubbles = useSeatBubbles();
  if (!game) return null;
  const view = crazyEightsViewFor(game, HUMAN_SEAT);
  const seatName = (s: BridgeSeat) => t(`cardgame-seat-${s}`);
  const suitName = (s: BridgeSuit) => `${SUIT_SYMBOL[s]} ${t(`bridge-suit-${s}`)}`;
  const legal = new Set(view.legalPlays.map(cardId));
  const others = view.seats.filter((s) => s !== HUMAN_SEAT);

  let status: string;
  if (view.phase === 'done') {
    status = result
      ? result.blocked
        ? t('ce-blocked')
        : result.winner === HUMAN_SEAT
          ? t('ce-hand-won-you', { points: result.points })
          : t('ce-hand-won', { seat: seatName(result.winner), points: result.points })
      : '';
  } else if (view.canAct) {
    status = view.canPass ? t('ce-drew-playable') : view.canDraw ? t('ce-turn-draw') : t('ce-turn-you');
  } else status = t('ce-turn-wait', { seat: seatName(view.turn) });

  const plate = (seat: BridgeSeat) => {
    const id = identify(seat);
    return (
      <SeatPlate
        bubble={bubbles[seat]?.line}
        testid={seat === HUMAN_SEAT ? 'ce-seat-S' : `ce-opponent-${seat}`}
        name={id.name}
        tag={id.tag}
        portrait={id.portrait}
        fallbackIcon={id.fallbackIcon}
        you={id.you}
        score={totals[seat] ?? 0}
        scoreLabel={`${seatName(seat)} ${totals[seat] ?? 0}`}
        cardsLeft={id.you ? undefined : (view.handSizes[seat] ?? 0)}
        cardsLeftLabel={t('ce-cards-left', { n: view.handSizes[seat] ?? 0 })}
        active={view.phase !== 'done' && view.turn === seat}
      />
    );
  };
  const lead = Math.max(...view.seats.map((x) => totals[x] ?? 0));
  const summary = result
    ? pointsSummary({
        round: handNumber,
        seats: view.seats,
        identify,
        totals,
        winner: result.winner,
        points: result.points,
        gameWinner,
        headline: gameWinner ? (gameWinner === HUMAN_SEAT ? t('ce-game-won') : t('ce-game-lost', { seat: seatName(gameWinner) })) : status,
      })
    : null;

  const scores = view.seats.map((s) => `${seatName(s)} ${totals[s] ?? 0}`).join(' · ');

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-4 lg:max-w-6xl lg:gap-6 lg:p-8 xl:max-w-7xl 2xl:max-w-[96rem]" data-testid="crazy-eights-screen" data-appdom-scope="crazy-eights">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold text-berry">{t('crazy-eights-title')}</h1>
        <div className="flex items-center gap-2 text-sm font-bold">
          <span className="text-fg-muted" data-testid="ce-seed">{t('ce-deal', { n: handNumber, seed })}</span>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={newGame} data-testid="ce-new-game" data-appdom-id="ce-new-game" data-appdom-type="Button">{t('ce-new-game')}</button>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={onExit} data-testid="ce-exit" data-appdom-id="ce-exit" data-appdom-type="Button">{t('bridge-exit')}</button>
        </div>
      </header>

      <output className="min-h-[48px] glass glass-light flex flex-col gap-1 rounded-xl px-3 py-2 text-sm font-bold text-ink" data-testid="ce-status" data-appdom-id="ce-status" data-appdom-type="Text" aria-live="polite">
        <span>{status}</span>
        <span className="font-normal text-fg-muted" data-testid="ce-match">{t('ce-match', { suit: suitName(view.activeSuit) })}</span>
        <span className="font-normal text-fg-muted" data-testid="ce-score">{t('ce-score', { scores, target: CRAZY_EIGHTS_TARGET })}</span>
      </output>

      <ScoreStrip
        testid="ce-score-strip"
        title={t('table-score')}
        note={t('table-first-to', { n: CRAZY_EIGHTS_TARGET })}
        rows={view.seats.map((x) => ({ key: x, label: identify(x).name, value: totals[x] ?? 0, you: x === HUMAN_SEAT, leading: lead > 0 && (totals[x] ?? 0) === lead }))}
      />

      <OpponentTable
        opponents={others.map((x) => ({ seat: x, node: plate(x) }))}
        center={
          <div className="flex flex-col items-center gap-4">
      <div className="flex items-center justify-center gap-6 lg:gap-12">
        <button
          type="button"
          onClick={draw}
          disabled={!view.canDraw}
          data-testid="ce-stock"
          data-appdom-id="ce-stock"
          data-appdom-type="Button"
          aria-label={`${t('ce-draw')}. ${t('ce-stock-left', { n: view.stockCount })}`}
          className="flex flex-col items-center gap-1 rounded-xl p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring enabled:hover:-translate-y-0.5 disabled:opacity-60"
        >
          <CardBack className={CARD_WIDTH_CLASS} />
          <span className="text-xs font-bold text-fg-muted">{view.canDraw ? t('ce-draw') : t('ce-stock-left', { n: view.stockCount })}</span>
        </button>
        {/* biome-ignore lint/a11y/useSemanticElements: a layout cell named for screen readers; a fieldset would bring its own box styling */}
        <div className="flex flex-col items-center gap-1" data-testid="ce-discard" role="group" aria-label={t('ce-pile')}>
          <PlayingCard card={view.top} fourColour={fourColour} />
          <span className="text-xs font-bold text-fg-muted">{t('ce-pile')}</span>
        </div>
        {view.canPass && (
          <button type="button" onClick={pass} data-testid="ce-pass" data-appdom-id="ce-pass" data-appdom-type="Button" className="min-h-[48px] rounded-xl border border-border px-4 py-2 font-display font-semibold">
            {t('ce-pass')}
          </button>
        )}
      </div>

      {pendingEight && (
        // biome-ignore lint/a11y/useSemanticElements: a flex row of buttons; a fieldset would bring its own box styling
        <div role="group" aria-label={pendingEight.rank === 15 ? t('ce-pick-suit-joker') : t('ce-pick-suit')} className="flex flex-wrap items-center justify-center gap-2" data-testid="ce-suit-choices">
          <span className="w-full text-center text-sm font-bold">{pendingEight.rank === 15 ? t('ce-pick-suit-joker') : t('ce-pick-suit')}</span>
          {SUITS.map((x) => (
            <button key={x} type="button" data-testid={`ce-suit-${x}`} onClick={() => chooseSuit(x)} className="min-h-[48px] rounded-xl border border-border bg-surface-raised px-4 py-2 font-display text-base font-semibold">
              {suitName(x)}
            </button>
          ))}
          <button type="button" onClick={cancelEight} data-testid="ce-suit-cancel" className="min-h-[48px] rounded-xl px-3 py-2 text-sm underline">
            {t('ce-cancel')}
          </button>
        </div>
      )}

          </div>
        }
        bottom={
      <section aria-label={t('bridge-hand-of', { seat: seatName(HUMAN_SEAT) })} data-testid="ce-hand" className="flex flex-col items-center gap-3">
        {plate(HUMAN_SEAT)}
        <div className={HAND_CLASS}>
          {sortHand(view.hand).map((c) => (
            <PlayingCard key={cardId(c)} card={c} fourColour={fourColour} onPlay={legal.has(cardId(c)) ? () => tap(c) : undefined} dimmed={view.canAct && !legal.has(cardId(c))} />
          ))}
        </div>
      </section>

        }
      />

      {summary && (
        <RoundSummaryPanel
          testid="ce-result"
          headlineTestid="ce-game-over"
          summary={summary}
          nextLabel={gameWinner ? t('ce-new-game') : t('ce-next-hand')}
          onNext={gameWinner ? newGame : nextHand}
          nextTestid={gameWinner ? 'ce-play-again' : 'ce-next-hand'}
        />
      )}

      <details className="rounded-2xl border border-border bg-surface p-3" data-testid="ce-settings">
        <summary className="flex min-h-[48px] cursor-pointer items-center text-sm font-extrabold uppercase tracking-wide text-fg-muted">{t('bridge-robots')}</summary>
        <div className="mt-3 flex flex-col gap-3 text-sm font-bold">
          <div className="flex items-center gap-2">
            {(['easy', 'clever'] as const).map((s) => (
              <button key={s} type="button" aria-pressed={strength === s} data-testid={`ce-strength-${s}`} onClick={() => setStrength(s)} className={`min-h-[48px] rounded-lg border px-3 py-1 ${strength === s ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                {t(`bridge-${s}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-1">
            <span>{t('ce-players')}</span>
            <div className="flex items-center gap-2">
              {([2, 3, 4] as PlayerCount[]).map((n) => (
                <button key={n} type="button" aria-pressed={players === n} data-testid={`ce-players-${n}`} onClick={() => setPlayers(n)} className={`min-h-[48px] rounded-lg border px-3 py-1 ${players === n ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                  {t('ce-players-n', { n })}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={fourColour} onChange={(e) => setFourColour(e.target.checked)} data-testid="ce-four-colour" />
            {t('bridge-four-colour')}
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={jokers} onChange={(e) => setJokers(e.target.checked)} data-testid="ce-jokers" />
            {t('ce-jokers')}
          </label>
          <p className="font-normal text-fg-muted" data-testid="ce-jokers-hint">{t('ce-jokers-hint')}</p>
          <p className="font-normal text-fg-muted">{t('ce-teaching-note')}{jokers ? ` ${t('ce-teaching-jokers')}` : ''}</p>
        </div>
      </details>
    </div>
  );
}
